import { Archive, ArchiveRestore, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { confirmSheet } from "@/components/ui/action-sheet";
import { fmtAt } from "@/lib/crm";

export const plural = (n, w) => `${n} ${w}${n === 1 ? "" : "s"}`;

export async function deleteOrArchive(c, onDeleted, onArchived) {
  try {
    const { data: imp } = await api.get(`/customers/${c.id}/delete-impact`);
    if (imp.blocked) {
      const ok = await confirmSheet({ title: `Can't delete ${c.name}`, message: imp.message, confirm: "Archive Customer" });
      if (ok) { await api.post(`/customers/${c.id}/archive`, { archived: true }); toast.success(`${c.name} archived`); onArchived(); }
      return;
    }
    const ok = await confirmSheet({ title: `Delete ${c.name}?`, destructive: true, confirm: "Delete Customer",
      message: `This also deletes ${plural(imp.projects, "project")} and ${plural(imp.estimates, "estimate")}. An Admin can restore them from Recently deleted for 30 days.` });
    if (!ok) return;
    await api.delete(`/customers/${c.id}`);
    toast.success(`${c.name} deleted`);
    onDeleted();
  } catch (e) {
    toast.error(errorMessage(e));
  }
}

export function CustomerActions({ c, onDeleted, onChanged }) {
  if (!c.can_delete) return null;
  const unarchive = async () => {
    try { await api.post(`/customers/${c.id}/archive`, { archived: false }); toast.success(`${c.name} restored`); onChanged(); } catch (e) { toast.error(errorMessage(e)); }
  };
  const archive = async () => {
    if (!(await confirmSheet({ title: `Archive ${c.name}?`, message: "Archived customers are hidden from lists, search and reports. You can restore them any time.", confirm: "Archive Customer" }))) return;
    try { await api.post(`/customers/${c.id}/archive`, { archived: true }); toast.success(`${c.name} archived`); onChanged(); } catch (e) { toast.error(errorMessage(e)); }
  };
  const btn = "inline-flex min-h-[44px] items-center gap-2 rounded-full px-4 text-[15px] font-medium transition-colors";
  return (
    <div className="flex flex-wrap gap-2" data-testid="customer-actions">
      {c.archived
        ? <button data-testid="customer-unarchive-button" onClick={unarchive} className={`${btn} bg-[#007AFF]/10 text-[#0062CC] hover:bg-[#007AFF]/15`}><ArchiveRestore className="h-4 w-4" /> Restore from archive</button>
        : <button data-testid="customer-archive-button" onClick={archive} className={`${btn} bg-black/[0.05] text-black hover:bg-black/[0.08]`}><Archive className="h-4 w-4" /> Archive</button>}
      <button data-testid="customer-delete-button" onClick={() => deleteOrArchive(c, onDeleted, onChanged)} className={`${btn} bg-[#FF3B30]/10 text-[#D70015] hover:bg-[#FF3B30]/15`}><Trash2 className="h-4 w-4" /> Delete customer</button>
    </div>
  );
}

const KIND = { edited: "Edited", archived: "Archived", unarchived: "Unarchived", restored: "Restored from Recently deleted", imported: "Imported", merged: "Merged" };

export function CustomerHistory({ items }) {
  if (!items?.length) return null;
  return (
    <section className="space-y-1.5" data-testid="customer-history">
      <p className="px-4 text-[13px] uppercase text-[#6D6D72]">History</p>
      <div className="overflow-hidden rounded-[12px] bg-white">
        {items.map((e, i) => (
          <div key={e.id} data-testid={`customer-history-${i}`} className="border-b border-black/[0.08] px-4 py-3 last:border-0">
            <p className="text-[15px] text-black"><span className="font-semibold">{KIND[e.kind] || e.kind}</span> · {e.by_name}{e.from_customer ? ` · from ${e.from_customer}` : ""}</p>
            {e.note && <p className="text-[13px] text-[#3C3C43]/80">{e.note}</p>}
            {e.changes?.map((ch) => (
              <p key={ch.field} className="text-[13px] text-[#3C3C43]/80">{ch.field}: <span className="line-through decoration-[#C7C7CC]">{ch.from || "—"}</span> → {ch.to || "—"}</p>
            ))}
            <p className="text-[12px] text-[#6D6D72]">{fmtAt(e.at)}</p>
          </div>
        ))}
      </div>
    </section>
  );
}
